import { useQuery } from '@apollo/client';
import RoleIcon from '@clearkit/icons/glyphs/Job';
import LocationIcon from '@clearkit/icons/glyphs/Location';
import Security from '@clearkit/icons/glyphs/Security';
import SeniorityIcon from '@clearkit/icons/glyphs/Seniority';
import TitleIcon from '@clearkit/icons/glyphs/String';
import { CKBox } from 'clearkit';
import React, { useState } from 'react';

import { SALESFORCE_RESOURCE_TYPE } from '~/components/SearchBuilder/utils/constants';
import SalesforceLogo from '~/images/destinations/Salesforce.svg';
import { destinationTypeSalesforceCreateLead } from '~/lib/destinations';
import { readableSingularSegmentType } from '~/lib/segments';
import Disconnected from '~/pages/destinations/components/Disconnected';
import FormHeader from '~/pages/destinations/components/FormHeader';
import TriggerType from '~/pages/destinations/components/TriggerType';

import {
  DEFAULT_MAX_RECORDS,
  MaxRecordsField,
} from '../components/MaxRecordsField';
import PredicateSelector from '../components/PredicateSelector';
import TraitConfiguration from '../components/TraitConfiguration';
import query from './query.graphql';

function removeEmpty(object) {
  return Object.keys(object)
    .filter((k) => object[k]?.length > 0)
    .reduce((a, k) => ({ ...a, [k]: object[k] }), {});
}

function CreateExternalRecordsLeadSalesforce({
  errors,
  refetch,
  accountConnectors,
  destination,
  segment,
  FormFooter,
}) {
  const accountConnector = accountConnectors.salesforce;
  const isNew = !destination;
  const initialConfig = isNew ? {} : destination.config;
  const humanizedSegmentType = readableSingularSegmentType(segment.type);

  const initialState = {
    traits: initialConfig.traits || [],
    triggerType: isNew ? 'segment_enter' : destination.triggerType,
    prospectSearch: {
      role: initialConfig.prospectSearch?.role || [],
      seniority: initialConfig.prospectSearch?.seniority || [],
      title: initialConfig.prospectSearch?.title || [],
      country: initialConfig.prospectSearch?.country || [],
      pageSize: initialConfig.prospectSearch?.pageSize || DEFAULT_MAX_RECORDS,
    },
  };

  const nameTraits = [
    'First Name',
    'Last Name',
    'Full Name',
    'Name',
    'Given Name',
    'Family Name',
  ];
  const companyTraits = ['Employment Name', 'Employment Domain'];
  const mappedTraitLabels = {
    OwnerId: 'Lead Owner',
    Type: 'Lead Type',
  };
  const [traits, setTraits] = useState(initialState.traits);
  const [triggerType, setTriggerType] = useState(initialState.triggerType);

  const [role, setRole] = useState(initialState.prospectSearch.role);
  const [seniority, setSeniority] = useState(
    initialState.prospectSearch.seniority,
  );
  const [title, setTitle] = useState(initialState.prospectSearch.title);
  const [companyLocation, setCompanyLocation] = useState(
    initialState.prospectSearch.country,
  );
  const [maxRecords, setMaxRecords] = useState(
    initialState.prospectSearch.pageSize,
  );

  const { data, loading } = useQuery(query);

  if (!accountConnector || accountConnector?.status !== 'active') {
    return (
      <Disconnected
        service="salesforce"
        onSuccess={refetch}
        Logo={SalesforceLogo}
        FormFooter={FormFooter}
      />
    );
  }

  if (loading) {
    return null;
  }

  return (
    <>
      <FormHeader
        customTitle={'Create new leads in Salesforce'}
        destinationType={destinationTypeSalesforceCreateLead}
        destinationName="Salesforce"
        destinationFlowDescription={'Configuration Destination'}
        segmentName={segment.name}
        segmentCollection={segment.collection.name}
        subtitle={
          'Discover people at companies in this audience and automatically create Salesforce leads'
        }
      />

      <CKBox
        className={'py-3 flex flex-1 rounded-none shadow-none'}
        style={{ background: '#fafbfc' }}>
        <div className={'mx-6'}>
          <div
            className={'flex basis-1 items-start shrink-1 mr-5 flex-col'}
            style={{ boxShadow: 'none' }}>
            <span
              className={'text-base font-medium shrink-0'}
              style={{ color: '#27282a' }}>
              Trigger
            </span>
            <span
              className={
                'text-ellipsis whitespace-nowrap overflow-hidden max-w-full text-sm'
              }
              style={{ color: '#596168', marginTop: '4px' }}>
              Choose when this destination will be run.{' '}
              <a
                href="https://help.clearbit.com/hc/en-us/articles/360023453694"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline' }}>
                Learn more
              </a>
            </span>
          </div>

          <TriggerType
            className={'external-account-trigger-type'}
            errors={errors}
            handleChange={(_, val) => setTriggerType(val)}
            isDisabled
            triggerType={triggerType}
            triggerTypes={[
              {
                value: 'segment_enter',
                label: (
                  <span style={{ color: '#596168' }}>
                    When a {humanizedSegmentType} <strong>enters</strong> the
                    audience
                  </span>
                ),
              },
            ]}
          />
        </div>
      </CKBox>
      <CKBox
        className={'py-3 flex flex-1 rounded-none shadow-none'}
        style={{ background: '#fafbfc' }}>
        <div className={'mx-6'}>
          <div className={'flex basis-1 items-start shrink-1 mr-5 flex-col'}>
            <span
              className={'text-base font-medium shrink-0'}
              style={{ color: '#27282a' }}>
              Prospect for people to create as leads
            </span>
            <span
              className={
                'text-ellipsis whitespace-nowrap overflow-hidden max-w-full text-sm'
              }
              style={{ color: '#596168', marginTop: '4px' }}>
              For companies in this audience, discover people not in your CRM
              who match the following criteria.
            </span>
          </div>
          <CKBox className={'p-6 mt-3 flex gap-4'}>
            <PredicateSelector
              icon={<RoleIcon />}
              typeName="role"
              placeholder="Choose roles"
              selectedItems={role}
              onSelectedItemsChange={setRole}
              options={data.prospectFilters.role}
            />
            <PredicateSelector
              icon={<SeniorityIcon />}
              typeName="seniority"
              placeholder="Choose seniorities"
              selectedItems={seniority}
              onSelectedItemsChange={setSeniority}
              options={data.prospectFilters.seniority}
            />
            <PredicateSelector
              icon={<TitleIcon />}
              typeName="title"
              placeholder="Choose titles"
              selectedItems={title}
              onSelectedItemsChange={setTitle}
              options={data.prospectFilters.title}
            />
            <PredicateSelector
              icon={<LocationIcon />}
              typeName="company location"
              placeholder="Choose company locations"
              selectedItems={companyLocation}
              onSelectedItemsChange={setCompanyLocation}
              options={data.prospectFilters.country}
              style={{ display: 'none' }}
            />
            <MaxRecordsField
              label="Max leads"
              value={maxRecords}
              onChange={setMaxRecords}
            />
            <hr />
            <div className={'text-sm italic text-gray-500 flex gap-2'}>
              <Security />
              <span>
                For GDPR compliance, we currently do not support prospecting for
                persons identified as being based in the EU.
              </span>
            </div>
          </CKBox>
        </div>
      </CKBox>
      <CKBox
        className={'py-3 flex flex-1 rounded-none shadow-none'}
        style={{ background: '#fafbfc' }}>
        <div className={'mx-6'}>
          <div className={'flex basis-1 items-start shrink-1 mr-5 flex-col'}>
            <span
              className={'text-base font-medium shrink-0'}
              style={{ color: '#27282a' }}>
              Create Salesforce leads
            </span>
            <span
              className={
                'text-ellipsis whitespace-nowrap overflow-hidden max-w-full text-sm'
              }
              style={{ color: '#596168', marginTop: '4px' }}>
              Choose the values to use for data fields when creating new leads
              in Salesforce.
            </span>
          </div>
        </div>
      </CKBox>

      <TraitConfiguration
        salesforceResourceType={SALESFORCE_RESOURCE_TYPE.LEAD}
        salesforceTraits={data.salesforceTraits}
        errors={errors}
        mappedTraitLabels={mappedTraitLabels}
        traits={traits}
        setTraits={setTraits}
        unsettableValues={[
          {
            trait: 'Lead email address',
            value: 'Person email address',
          },
          {
            trait: 'Lead source',
            value: 'Clearbit created lead',
          },
        ]}
        companyAttributes={data.companyAttributes}
        companyTraits={companyTraits}
        personAttributes={data.personAttributes}
        nameTraits={nameTraits}
      />

      <FormFooter
        destinationConfig={{
          config: {
            traits,
            prospect_search: {
              ...removeEmpty({
                role,
                title,
                seniority,
                country: companyLocation,
              }),
              page_size: maxRecords,
            },
          },
          triggerType,
        }}
      />
    </>
  );
}

export default CreateExternalRecordsLeadSalesforce;
